import { TuiElasticContainer } from '@taiga-ui/layout';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { TuiAccordion, TuiTextarea, TuiButtonLoading } from '@taiga-ui/kit';
import { DeviceDetectorService, XAlertService } from '@core/services';
import { RequestService } from '@core/services/request/request.service';
import { WorkflowService } from '@core/services/workflow/workflow.service';
import {
  CreateWorkflowSuccessResponse,
  RequestWorkflow,
  Workflow,
  WorkflowInput,
} from '@features/requests/request-form/components/request-form-workflows/types';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TuiButton, TuiHint, TuiScrollbar, TuiTextfield } from '@taiga-ui/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, Subject, switchMap } from 'rxjs';
import { InputPhotosService } from '@core/services/workflow/input-photos.service';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { WorkflowImportError } from './workflow-import.error';
import { environment } from '@environment/environment';
import { OutkeepPermissionService } from '@core/services/permission';

@Component({
  selector: 'x-workflow-debug',
  imports: [
    TuiButtonLoading,
    TuiTextfield,
    CommonModule,
    FormsModule,
    TuiElasticContainer,
    TuiAccordion,
    TuiButton,
    TuiHint,
    TuiTextarea,
    TuiScrollbar,
  ],
  templateUrl: './workflow-debug.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Компонент для отладки регламентов заявки
 * Позволяет:
 * - Просматривать все доступные регламенты в системе
 * - Прикреплять/открепять регламенты к заявке
 * - Сбрасывать данные регламентов для заявки
 */
export class WorkflowDebugComponent {
  private readonly requestService = inject(RequestService);
  private readonly workflowService = inject(WorkflowService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alertService = inject(XAlertService);
  private readonly inputPhotosService = inject(InputPhotosService);
  private readonly permissionService = inject(OutkeepPermissionService);

  readonly deviceService = inject(DeviceDetectorService);

  readonly $request = this.requestService.$request;

  readonly $canManageWorkflowsSetup = toSignal(
    this.permissionService.canManageWorkflowsSetup$,
    { initialValue: false },
  );

  // Trigger для перезагрузки списка всех регламентов
  private readonly refreshAllWorkflows$ = new Subject<void>();

  // Все доступные регламенты в системе
  readonly $allWorkflows = toSignal(
    this.refreshAllWorkflows$.pipe(
      switchMap(() => this.workflowService.getAllWorkflows()),
    ),
    { initialValue: [] as Workflow[] },
  );

  // Регламенты, прикрепленные к текущей заявке
  readonly $requestWorkflows = signal<RequestWorkflow[]>([]);

  // ID выбранных регламентов для прикрепления
  readonly $selectedWorkflowIds = signal<Set<string>>(new Set());

  // Состояния загрузки для различных операций
  readonly $isLoadingAttach = signal(false);
  readonly $isLoadingDetach = signal(false);
  readonly $isLoadingClear = signal(false);
  readonly $isLoadingImport = signal(false);

  // ID регламентов, которые сейчас открепляются
  readonly $detachingWorkflowIds = signal<Set<string>>(new Set());

  // ID регламента, который сейчас удаляется
  readonly $deletingWorkflowId = signal<string | null>(null);

  // Состояния раскрытия аккордеонов
  readonly $accordionStates = {
    allWorkflows: signal(false),
    requestWorkflows: signal(false),
    importWorkflow: signal(false),
  };

  // Состояние для импорта регламента
  readonly $showImportUI = signal(false);
  readonly $importJsonInput = signal('');

  constructor() {
    this.refreshAllWorkflows$.next(); // Загружаем регламенты при инициализации
    this.loadRequestWorkflows();
  }

  /**
   * Перезагружает список всех доступных регламентов в системе
   */
  private refreshAllWorkflows(): void {
    this.refreshAllWorkflows$.next();
  }

  /**
   * Загружает список регламентов, прикрепленных к текущей заявке
   */
  private loadRequestWorkflows(): void {
    this.workflowService
      .getAllRequestWorkflowsOfRequest(this.$request().requestNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(workflows => this.$requestWorkflows.set(workflows));
  }

  /**
   * Вспомогательный метод для обновления Set в сигнале
   * Создает новый Set, применяет к нему action и обновляет сигнал
   */
  private updateSet<T>(
    setSignal: { (): Set<T>; set: (value: Set<T>) => void },
    action: (set: Set<T>) => void,
  ): void {
    const newSet = new Set(setSignal());

    action(newSet);
    setSignal.set(newSet);
  }

  /**
   * Переключает выбор регламента для прикрепления
   * Не позволяет выбирать уже прикрепленные регламенты
   */
  toggleWorkflowSelection(workflowId: string): void {
    if (this.isWorkflowAttached(workflowId)) return;

    this.updateSet(this.$selectedWorkflowIds, set => {
      if (set.has(workflowId)) {
        set.delete(workflowId);
      } else {
        set.add(workflowId);
      }
    });
  }

  /**
   * Проверяет, выбран ли регламент для прикрепления
   */
  isWorkflowSelected(workflowId: string): boolean {
    return this.$selectedWorkflowIds().has(workflowId);
  }

  /**
   * Проверяет, прикреплен ли регламент к текущей заявке
   */
  isWorkflowAttached(workflowId: string): boolean {
    return this.$requestWorkflows().some(
      rw => rw.workflowVersion?.workflowId === workflowId,
    );
  }

  /**
   * Проверяет, открепляется ли сейчас конкретный регламент
   */
  isWorkflowDetaching(requestWorkflowId: string): boolean {
    return this.$detachingWorkflowIds().has(requestWorkflowId);
  }

  /**
   * Открепляет один конкретный регламент от заявки
   * Использует attachSelectedWorkflows с отфильтрованным списком ID
   */
  detachWorkflow(requestWorkflowId: string): void {
    const workflow = this.$requestWorkflows().find(
      rw => rw.id === requestWorkflowId,
    );
    const workflowName =
      workflow?.workflowVersion?.workflow?.publicName || 'Без имени';

    this.alertService
      .showDialog(
        'Подтверждение открепления',
        `Вы уверены, что хотите открепить регламент <b>${workflowName}</b>?`,
      )
      .pipe(
        filter(confirmed => Boolean(confirmed)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // Получаем ID всех регламентов, кроме открепляемого
        const workflowIdsToKeep = this.$requestWorkflows()
          .filter(rw => rw.id !== requestWorkflowId)
          .map(rw => rw.workflowVersion?.workflowId)
          .filter((id): id is string => id !== undefined);

        // Добавляем ID в список открепляемых для отображения лоадера
        this.updateSet(this.$detachingWorkflowIds, set =>
          set.add(requestWorkflowId),
        );

        // Прикрепляем только оставшиеся регламенты (тем самым открепляя текущий)
        this.workflowService
          .attachSelectedWorkflows(
            this.$request().requestNumber,
            workflowIdsToKeep,
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.loadRequestWorkflows();
              this.updateSet(this.$detachingWorkflowIds, set =>
                set.delete(requestWorkflowId),
              );
            },
            error: () => {
              this.updateSet(this.$detachingWorkflowIds, set =>
                set.delete(requestWorkflowId),
              );
            },
          });
      });
  }

  /**
   * Прикрепляет выбранные регламенты к заявке
   * Объединяет уже прикрепленные регламенты с новыми выбранными
   */
  attachSelectedWorkflows(): void {
    const selectedIds = Array.from(this.$selectedWorkflowIds());

    if (selectedIds.length === 0) return;

    // Получаем ID уже прикрепленных регламентов
    const alreadyAttachedIds = this.$requestWorkflows()
      .map(rw => rw.workflowVersion?.workflowId)
      .filter((id): id is string => id !== undefined);

    // Объединяем существующие и новые ID (Set автоматически удалит дубликаты)
    const allWorkflowIds = [
      ...new Set([...alreadyAttachedIds, ...selectedIds]),
    ];

    this.$isLoadingAttach.set(true);

    this.workflowService
      .attachSelectedWorkflows(this.$request().requestNumber, allWorkflowIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.$selectedWorkflowIds.set(new Set());
          this.workflowService.refreshEntities();
          this.loadRequestWorkflows();
          this.$isLoadingAttach.set(false);
        },
        error: () => this.$isLoadingAttach.set(false),
      });
  }

  /**
   * Открепляет все регламенты от заявки
   * Использует attachSelectedWorkflows с пустым массивом
   */
  detachAllWorkflows(): void {
    this.alertService
      .showDialog(
        'Подтверждение открепления',
        'Вы уверены, что хотите открепить <b>все регламенты</b>?',
      )
      .pipe(
        filter(confirmed => Boolean(confirmed)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.$isLoadingDetach.set(true);

        // Передаем пустой массив для открепления всех регламентов
        this.workflowService
          .attachSelectedWorkflows(this.$request().requestNumber, [])
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.loadRequestWorkflows();
              this.workflowService.refreshEntities();
              this.$isLoadingDetach.set(false);
            },
            error: () => this.$isLoadingDetach.set(false),
          });
      });
  }

  /**
   * Сбрасывает все данные регламентов для текущей заявки
   * Удаляет все сабмиты и прогресс выполнения
   */
  clearAllWorkflowsData(): void {
    this.$isLoadingClear.set(true);

    const requestNumber = this.$request().requestNumber;

    this.workflowService
      .clearAllWorkflowsDataForRequest(requestNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loadRequestWorkflows();
          this.workflowService.refreshEntities();
          this.workflowService.clearWorkflowDataForRequest(requestNumber);
          this.inputPhotosService.clearPhotoFiles();
          this.$isLoadingClear.set(false);
        },
        error: () => this.$isLoadingClear.set(false),
      });
  }

  /**
   * Переключает отображение UI для импорта регламента
   */
  toggleImportUI(): void {
    const newValue = !this.$showImportUI();

    this.$showImportUI.set(newValue);

    if (newValue) {
      this.$accordionStates.importWorkflow.set(true);
    } else {
      this.$importJsonInput.set('');
    }
  }

  /**
   * Отменяет импорт и скрывает UI
   */
  cancelImport(): void {
    this.$showImportUI.set(false);
    this.$importJsonInput.set('');
  }

  private getImportErrorMessage(error: unknown): string {
    if (error instanceof WorkflowImportError) {
      if (error.errors?.length) {
        return error.errors
          .map(e => {
            const location = e.path?.length ? e.path.join('.') : e.field;

            return `${location}: ${e.rule}`;
          })
          .join('\n');
      }

      return error.failureReason;
    }

    return 'Ошибка при импорте регламента';
  }

  /**
   * Импортирует новый регламент из JSON
   * Валидирует JSON перед отправкой на сервер
   */
  importWorkflow(): void {
    const jsonString = this.$importJsonInput().trim();

    if (!jsonString) {
      this.alertService.error('Введите JSON для импорта');

      return;
    }

    let parsed: unknown;

    try {
      parsed = JSON.parse(jsonString);
    } catch {
      this.alertService.error('Невалидный JSON формат');

      return;
    }

    if (!parsed || typeof parsed !== 'object' || !('workflow' in parsed)) {
      this.alertService.error('Нет данных для регламента');

      return;
    }

    const workflowJson = (parsed as { workflow: WorkflowInput }).workflow;

    this.$isLoadingImport.set(true);

    const publish = true;

    this.workflowService
      .importWorkflow(workflowJson, publish)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            const body = err.error as any;
            const failureReason =
              typeof body?.failureReason === 'string'
                ? body.failureReason.trim()
                : '';
            const errors = Array.isArray(body?.errors)
              ? body.errors
              : undefined;

            if (failureReason || errors?.length) {
              throw new WorkflowImportError(
                failureReason || 'Ошибка при импорте регламента',
                errors,
              );
            }
          }
          throw err;
        }),
      )
      .subscribe({
        next: (result: CreateWorkflowSuccessResponse) => {
          this.$isLoadingImport.set(false);
          this.alertService.success(
            `Регламент успешно создан (ID: ${result.data.id})`,
          );
          this.cancelImport();
          // Перезагружаем список всех регламентов
          this.refreshAllWorkflows();
          this.$accordionStates.importWorkflow.set(false);
        },
        error: (error: unknown) => {
          this.$isLoadingImport.set(false);
          const errorMessage = this.getImportErrorMessage(error);

          console.error('[Import Workflow error]:\n', errorMessage);
          this.alertService.error(errorMessage);
        },
      });
  }

  isDevelopMode() {
    return !environment.production;
  }

  deleteWorkflow(workflowId: string): void {
    if (this.isWorkflowAttached(workflowId)) {
      return;
    }

    const workflow = this.$allWorkflows().find(w => w.id === workflowId);
    const workflowName = workflow?.publicName || 'Без имени';

    this.alertService
      .showDialog(
        'Подтверждение удаления',
        `Вы уверены, что хотите удалить регламент <b>${workflowName}</b>? Это действие нельзя будет отменить.`,
      )
      .pipe(
        filter(confirmed => Boolean(confirmed)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.$deletingWorkflowId.set(workflowId);

        this.workflowService
          .removeWorkflow(workflowId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: response => {
              if (!response.isOk) {
                this.alertService.error(
                  `Не удалось удалить регламент: ${response.failureReason}`,
                );
                this.$deletingWorkflowId.set(null);

                return;
              }
              this.alertService.success('Регламент успешно удален');
              this.refreshAllWorkflows();
              this.$deletingWorkflowId.set(null);
            },
            error: () => {
              this.alertService.error('Не удалось удалить регламент');
              this.$deletingWorkflowId.set(null);
            },
          });
      });
  }
}
